import {
  Add,
  Camera,
  EventNote,
  GroupAdd,
  RemoveRedEyeSharp,
  Settings,
  SpeakerGroup,
  TextSnippet,
} from '@mui/icons-material';
import { Box, Typography } from '@mui/material';
import ButtonActionsComponent from 'components/ButtonActionsComponent';
import ButtonComponent from 'components/ButtonComponent';
import ConfirmDeleteModalComponent from 'components/ConfirmDeleteModalComponent';
import DeviceEditionComponent from 'components/DeviceEditionComponent';
import LogsModalComponent from 'components/LogsComponent';
import TableComponent, { IColumnProps } from 'components/TableComponent';
import { AppContext } from 'context/AppContextProvider';
import React, { useContext, useEffect, useState } from 'react';
import {
  useDeleteDeviceByIdAsync,
  useGetAllCamsByLicenseCountAsync,
  useGetDeviceByIdAsync,
} from 'services/Devices/hooks/useMutation';
import {
  DeviceDiscriminator,
  IDeviceProps,
  IDeviceSimpleProps,
} from 'services/Devices/types';
import { IPaginationParams } from 'services/types';
import DeviceConfigModal from '../_components/DeviceConfigModal';
import DeviceDetailsByDiscriminator from '../_components/DeviceDetailsByDiscriminator';
import BrandIcons from 'pages/DevicesPages/_components/BrandIcons';
import { useGetIvsDeviceByIdAsync } from '../../../services/Devices/IvsService/hooks/useMutation';
import { useGetLprDeviceByIdAsync } from '../../../services/Devices/LprService/hooks/useMutation';
import { useGetRecognitionCameraByIdAsync } from '../../../services/Devices/Recognition/Cameras/Intelbras/RecognitionCameraService/hooks/useMutation';
import HeaderDashboard from 'pages/DashboardPageClient/_components/_ui/Header';
import { IDeviceStreamModalProps } from '../_components/types';
import IsOnlineComponent from 'components/IsOnlineComponent';
import TooltipComponent from 'components/TooltipComponent';
import { DEVICE_CAMS_ENDPOINT } from 'services/Devices';
import { getStreamByEndpointAsync } from 'services/Events';
import DeviceLogStream from '../_components/DeviceLogStream';
import DeviceStreamModal from '../_components/DeviceStreamModal';
import EventListByDeviceModal from '../_components/EventListByDeviceModal';
import WebRTCPlayerOvenMedia from '../../../components/WebRTCPlayerOvenMedia';
import GroupManagementModal from '../_components/GroupManagementModal';
import { t } from 'i18next';

const CamsPage = () => {
  const [isVisibleEditionModal, setIsVisibleEditionModal] = useState(false);
  const [deviceStream, setDeviceStream] = useState<IDeviceStreamModalProps>({
    isVisible: false,
    isOnline: false,
    isDirectConnection: true,
  });
  const [selectedDevice, setSelectedDevice] = useState<IDeviceProps>(
    {} as IDeviceProps,
  );
  const [filter, setFilter] = useState<any>({
    name: '',
  });
  const [isLoadingCams, setIsLoadingCams] = useState<boolean>(false);
  const { showSnackbar } = useContext(AppContext);
  const [devicePaginationParams, setDevicePaginationParams] =
    useState<IPaginationParams>({ pageNumber: 1, pageSize: 10 });
  const [isVisibleConfigModal, setIsVisibleConfigModal] =
    useState<boolean>(false);
  const [isVisibleGroupManagement, setIsVisibleGroupManagement] =
    useState(false);
  const [eventsModal, setEventsModal] = useState<{
    isVisible: boolean;
    deviceId?: number;
    discriminator?: DeviceDiscriminator;
    deviceName?: string;
  }>({
    isVisible: false,
    deviceId: undefined,
    discriminator: undefined,
    deviceName: undefined,
  });

  const [isVisibleDeleteItemModal, setIsVisibleDeleteItemModal] =
    useState<boolean>(false);

  const [selectedItemToDelete, setSelectedItemToDelete] = useState<
    IDeviceSimpleProps | undefined
  >();

  const [logModal, setLogModal] = useState<{
    isVisible: boolean;
    deviceId: number;
  }>({ isVisible: false, deviceId: 0 });
  const [devices, setDevices] = useState<IDeviceSimpleProps[]>([]);
  const [devicesCount, setDevicesCount] = useState<number>(0);
  const columns: IColumnProps[] = [
    { label: 'Ref.', align: 'center', id: 'id', width: 0 },
    {
      label: t('common.name'),
      id: 'id',
      render(value, rowIndex) {
        const searched = devices.find((v) => v.id == Number.parseInt(value));
        if (searched) {
          return (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.3 }}>
              <IsOnlineComponent
                width={18}
                height={18}
                isOnline={Boolean(searched.isOnline)}
              />
              {searched.name}
            </Box>
          );
        }
        return <></>;
      },
    },
    { label: t('common.model'), id: 'model' },
    {
      label: t('common.brand'),
      width: 0,
      align: 'center',
      id: 'brand',
      render(val: any) {
        return <BrandIcons brand={val} />;
      },
    },
    {
      label: t('common.type'),
      id: 'id',
      align: 'center',
      width: 0,
      render(value) {
        const searched = devices.find((v) => v.id == Number.parseInt(value));
        if (searched) {
          const dev = DeviceDetailsByDiscriminator(searched.discriminator);
          return (
            <TooltipComponent title={dev?.typeDescription}>
              <Typography color={dev?.color ?? 'primary'}>
                {dev?.icon}
              </Typography>
            </TooltipComponent>
          );
        }
        return <></>;
      },
    },
    {
      id: 'id',
      label: t('common.actions'),
      align: 'center',
      width: 0,
      render: (id) => (
        <ButtonActionsComponent
          key={id}
          isWithDefaultOptionsList
          actionOptions={actionOptions(Number(id))}
          handleDelete={() => handleDeleteItem(Number(id))}
          handleEdit={() => handleEdit(Number(id))}
        />
      ),
    },
  ];

  const actionOptions = (id: number) => {
    return [
      {
        label: t('actionsOptions.configuration'),
        icon: <Settings />,
        onClick: () => {
          setIsVisibleConfigModal(true);
          handleConfig(id);
        },
      },
      {
        label: 'Logs',
        icon: <TextSnippet />,
        onClick: () => {
          setLogModal({ isVisible: true, deviceId: id });
        },
      },
      {
        label: t('actionsOptions.viewRecording'),
        icon: <RemoveRedEyeSharp fontSize="small" />,
        onClick: () => {
          if (devices) {
            const device = devices.find((d) => d.id === id);
            if (device) {
              setDeviceStream((oldStt) => ({
                deviceName: device.name,
                deviceId: device.id,
                isDirectConnection: device.connection == 0,
                isVisible: true,
                isOnline: device.isOnline,
              }));
            }
          }
        },
      },
      {
        label: t('actionsOptions.viewEvents'),
        icon: <EventNote fontSize="small" />,
        onClick: () => {
          if (devices) {
            const device = devices.find((d) => d.id === id);
            if (device !== null)
              setEventsModal((oldV) => ({
                ...oldV,
                isVisible: true,
                deviceId: device?.id,
                discriminator: device?.discriminator,
                deviceName: device?.name,
              }));
          }
        },
      },
    ];
  };

  const brands: any[] = [{ label: 'Intelbras', value: 'INTELBRAS' }];

  const { mutateAsync: mutateGetDeviceById } = useGetDeviceByIdAsync();
  const {
    mutateAsync: mutateDeleteDeviceById,
    isLoading: isLoadingDeleteDeviceById,
  } = useDeleteDeviceByIdAsync();
  const { mutateAsync: mutateGetAllCamsCount } =
    useGetAllCamsByLicenseCountAsync();

  const handleEdit = (id: number) => {
    mutateGetDeviceById(id).then(setSelectedDevice);
    setIsVisibleEditionModal(true);
  };

  const { mutateAsync: mutateGetIvsDeviceById } = useGetIvsDeviceByIdAsync();
  const { mutateAsync: mutateGetLprDeviceById } = useGetLprDeviceByIdAsync();
  const { mutateAsync: mutateGetRecognitionCameraById } =
    useGetRecognitionCameraByIdAsync();
  const handleConfig = async (id: number) => {
    const dev = devices.find((v) => v.id === id);
    if (dev) {
      switch (dev.discriminator) {
        case 'IvsCameraEntity':
          await mutateGetIvsDeviceById(dev.id).then((val) => {
            val.discriminator = 'IvsCameraEntity';
            setSelectedDevice(val);
          });
          break;
        case 'LprCameraEntity':
          await mutateGetLprDeviceById(dev.id).then((val) => {
            val.discriminator = 'LprCameraEntity';
            setSelectedDevice(val);
          });
          break;
        case 'RecognitionCameraEntity':
          await mutateGetRecognitionCameraById(dev.id).then((val) => {
            val.discriminator = 'RecognitionCameraEntity';
            setSelectedDevice(val);
          });
          break;
        default:
          console.warn('Discriminator não tratado:', dev.discriminator);
      }
    }
    setIsVisibleConfigModal(true);
  };

  const additionalButtons = (
    <Box display={'flex'} gap={2} justifyContent={'space-around'}>
      <ButtonComponent
        size={'small'}
        color={'secondary'}
        onClick={() => setIsVisibleGroupManagement(true)}
        startIcon={<SpeakerGroup />}
      >
        {t('camsPage.manageGroups')}
      </ButtonComponent>
      <ButtonComponent
        startIcon={<Add />}
        size="small"
        onClick={() => setIsVisibleEditionModal(true)}
      >
        {t('camsPage.addCam')}
      </ButtonComponent>
    </Box>
  );

  const handleCancelDeletion = () => {
    setIsVisibleDeleteItemModal(false);
    setSelectedItemToDelete(undefined);
  };

  const handleDeleteItem = async (id: number) => {
    const device: any = await mutateGetDeviceById(id);
    setSelectedItemToDelete(device);
    setIsVisibleDeleteItemModal(true);
  };

  const handleConfirmDeletion = () => {
    if (selectedItemToDelete?.id)
      mutateDeleteDeviceById(selectedItemToDelete?.id).then(() => {
        setDevicePaginationParams((oldStt) => ({ ...oldStt, pageNumber: 1 }));
        setIsVisibleDeleteItemModal(false);
        showSnackbar('Câmera excluída com sucesso.', 'success');
        getDevices(devicePaginationParams);
      });
  };

  const getDevices = (pagination: IPaginationParams) => {
    getStreamByEndpointAsync(
      DEVICE_CAMS_ENDPOINT,
      setDevices,
      devicePaginationParams,
      undefined,
      setIsLoadingCams,
      undefined,
      true,
      filter,
    );
    mutateGetAllCamsCount({ filter }).then(setDevicesCount);
  };

  useEffect(() => {
    getDevices(devicePaginationParams);
  }, [devicePaginationParams]);

  return (
    <>
      <HeaderDashboard></HeaderDashboard>
      <TableComponent
        title={
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Camera color="success" />
            <Typography fontSize={19}>{t('camsPage.title')}</Typography>
          </Box>
        }
        expandedRowContent={(row: any) => <DeviceLogStream deviceId={row.id} />}
        columns={columns}
        isExpandedRow
        onChangeMainFilter={(val) =>
          setFilter((oldSt: any) => ({ ...oldSt, name: val }))
        }
        onSubmitMainFilter={() => getDevices(devicePaginationParams)}
        totalCount={devicesCount}
        onChangePageOrRowsPerPage={(param) => {
          setDevicePaginationParams(param);
          getDevices(param);
        }}
        rows={devices}
        additionalTitleComponent={additionalButtons}
      />

      <DeviceEditionComponent
        isOpen={isVisibleEditionModal}
        deviceToEdit={selectedDevice}
        setDeviceToEdit={setSelectedDevice}
        isCam
        handleSaveForms={(_, reset) => {
          getDevices(devicePaginationParams);
          setIsVisibleEditionModal(false);
          setSelectedDevice({} as IDeviceProps);
          reset();
        }}
        discriminator={selectedDevice?.discriminator}
        availableEvents={
          DeviceDetailsByDiscriminator(selectedDevice?.discriminator)
            ?.availableEvents ?? []
        }
        setIsOpen={setIsVisibleEditionModal}
        brands={brands}
      />
      <DeviceStreamModal
        deviceStream={deviceStream}
        isDirectConnection={deviceStream.isDirectConnection}
        onClose={() =>
          setDeviceStream({ isVisible: false } as IDeviceStreamModalProps)
        }
      />

      <LogsModalComponent
        deviceId={logModal.deviceId}
        isVisible={logModal.isVisible}
        setIsVisible={(state) =>
          setLogModal((oldStt) => ({ ...oldStt, isVisible: state }))
        }
      />

      <DeviceConfigModal
        discriminator={selectedDevice?.discriminator}
        isOpen={isVisibleConfigModal}
        selectedDevice={selectedDevice}
        setSelectedDevice={setSelectedDevice}
        setIsOpen={setIsVisibleConfigModal}
      />

      <EventListByDeviceModal
        deviceName={eventsModal.deviceName}
        isOpen={eventsModal.isVisible}
        deviceId={eventsModal.deviceId}
        onClose={() =>
          setEventsModal((oldVal) => ({ ...oldVal, isVisible: false }))
        }
      />

      <ConfirmDeleteModalComponent
        isLoading={isLoadingDeleteDeviceById}
        isVisibleDeleteDialog={isVisibleDeleteItemModal}
        setIsVisibleDeleteDialog={setIsVisibleDeleteItemModal}
        title={'Excluir Dispositivo'}
        message={`Tem certeza que deseja excluir a câmera "${selectedItemToDelete?.name}"?`}
        handleCancelDeletion={handleCancelDeletion}
        handleConfirmDeletion={handleConfirmDeletion}
      />

      <GroupManagementModal
        isOpen={isVisibleGroupManagement}
        onClose={() => setIsVisibleGroupManagement(false)}
      />
    </>
  );
};

export default CamsPage;
